import { useAssignCase } from './useWorkQueue';
import { useUser } from '../providers/UserProvider';
import { useToast } from '../providers/ToastProvider';
import { reportError } from '../services/errorReporting';
import type { WorkQueueCase } from '../services/workQueue';

// `team`, when passed, targets that team's assignee slot (teamAssignees[team])
// instead of the case's overall assignee - additive on top of the existing
// single-team call sites, which omit it and keep today's exact behaviour.
export function useCaseAssignment(workCase: WorkQueueCase, source: string, team?: string) {
    const effectiveTeam = team ?? workCase.team;
    // Optional-chained: a backend predating teamAssignees omits the key, and
    // this runs under the app's single global error boundary.
    const currentAssignee = team ? (workCase.teamAssignees?.[team] ?? null) : workCase.assignee;
    const { user } = useUser();
    const { showToast } = useToast();
    const assignMutation = useAssignCase();

    const reportAssignmentError = (err: unknown, action: string): void => {
        const message = err instanceof Error ? err.message : 'Unknown error';
        reportError({
            source,
            message,
            stack: err instanceof Error ? err.stack : undefined,
            url: globalThis.location?.href ?? '',
            userAgent: navigator.userAgent,
            errorType: 'assignment_failure',
            metadata: { caseId: workCase.id, action }
        });
    };

    // Both handlers report success so callers holding local assignee state
    // (e.g. CompletionCard, whose case object is a snapshot) can sync it.
    const handleAssign = async (email: string): Promise<boolean> => {
        try {
            await assignMutation.mutateAsync({
                patientId: workCase.id,
                email,
                expectedAssignedTo: currentAssignee,
                team
            });
            return true;
        } catch (error) {
            showToast('Failed to assign case. Please try again.', 'error');
            reportAssignmentError(error, `assign:${email}`);
            return false;
        }
    };

    const handleUnassign = async (): Promise<boolean> => {
        try {
            await assignMutation.mutateAsync({
                patientId: workCase.id,
                email: null,
                expectedAssignedTo: currentAssignee,
                team
            });
            return true;
        } catch (error) {
            showToast('Failed to unassign case. Please try again.', 'error');
            reportAssignmentError(error, 'unassign');
            return false;
        }
    };

    // GP-owned cases can be assigned to anyone at ODO — GP work is shared
    // across teams rather than staffed by a dedicated role.
    const roles = effectiveTeam === 'GP' ? undefined : [effectiveTeam];

    return {
        roles,
        currentUserEmail: user?.email ?? '',
        handleAssign,
        handleUnassign
    };
}
